/**
 * Step 1: Search
 * Entry point for the self-serve media plan flow.
 * Composes BusinessSearchCard with Google Maps + Website URL tabs.
 * Triggers plan generation, stores result in flow context, navigates to plan.
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';
import PlacesAutocomplete from '@/components/PlacesAutocomplete';
import BusinessSearchCard from '@/components/BusinessSearchCard';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import { MapPin, Globe, Building2 } from 'lucide-react';
import mediaPlanApi, { isMockMode } from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';
import { toast } from 'sonner';

const TABS = [
  { id: 'search', label: 'Google Maps', icon: MapPin },
  { id: 'website', label: 'Website URL', icon: Globe },
];


export default function MediaPlanSearch() {
  const navigate = useNavigate();
  const { setBusiness: setFlowBusiness, setPlans, setTargetingAddress: setFlowTargetingAddress, reset } = useMediaPlanFlow();

  const [business, setBusiness] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('search');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [targetingAddress, setTargetingAddress] = useState('');

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const handlePlaceSelect = useCallback((info) => {
    setBusiness(info);
    setError(null);
  }, []);

  /**
   * Core flow: search business → get plans → store in context → navigate
   */
  const isValidUrl = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleContinue = useCallback(async () => {
    const isWebsite = activeTab === 'website';
    if (!isWebsite && !business) return;
    if (isWebsite && !isValidUrl(websiteUrl)) return;

    setIsLoading(true);
    setError(null);

    try {
      let resolvedBusiness;
      if (isWebsite) {
        // Scrape business from website URL
        resolvedBusiness = await mediaPlanApi.searchBusiness(websiteUrl);
      } else if (!business.placeId) {
        resolvedBusiness = await mediaPlanApi.searchBusiness(business.name);
      } else {
        resolvedBusiness = business;
      }

      setFlowBusiness(resolvedBusiness);

      // For website tab, pass the user-provided targeting address
      const planOptions = {};
      if (isWebsite && targetingAddress.trim()) {
        planOptions.targetingAddress = targetingAddress.trim();
        setFlowTargetingAddress(targetingAddress.trim());
      }

      const plans = await mediaPlanApi.getPlansForBusiness(
        resolvedBusiness.id || resolvedBusiness.placeId || resolvedBusiness.name,
        null,
        planOptions,
      );
      setPlans(plans);

      const recommended = plans.find((p) => p.recommended) || plans[0];
      navigate(`/campaign/${recommended.id}/plan`);
    } catch (err) {
      console.error('Search failed:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      toast.error('Failed to build plan', { description: err.message });
      setIsLoading(false);
    }
  }, [business, activeTab, websiteUrl, targetingAddress, navigate, setFlowBusiness, setFlowTargetingAddress, setPlans]);

  const handleMockDemo = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const mockBusiness = await mediaPlanApi.getBusinessByPlaceId('mock-place-autozone-001');
      setBusiness(mockBusiness);
      setFlowBusiness(mockBusiness);

      const plans = await mediaPlanApi.getPlansForBusiness(mockBusiness.placeId);
      setPlans(plans);

      const recommended = plans.find((p) => p.recommended) || plans[0];
      navigate(`/campaign/${recommended.id}/plan`);
    } catch (err) {
      console.error('Mock demo failed:', err);
      setError(err.message || 'Something went wrong.');
      setIsLoading(false);
    }
  }, [navigate, setFlowBusiness, setPlans]);

  // Reset flow on mount (fresh search = fresh flow)
  // Guard: don't reset if navigated from leads (leadId in persisted state)
  React.useEffect(() => {
    try {
      const persisted = JSON.parse(sessionStorage.getItem('cornercast_campaign_flow') || 'null');
      if (!persisted?.leadId) {
        reset();
      }
    } catch {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CTA state
  const hasSource = activeTab === 'search' ? !!business : isValidUrl(websiteUrl) && targetingAddress.trim().length > 0;

  return (
    <>
      <SelfServeHeader showNav={false} showAuth={false} />

      <BusinessSearchCard
        title="Get your business on streaming TV"
        subtitle="Tell us your business and we will build a complete ad campaign — targeted to your neighborhood, ready in minutes."
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        error={error}
        ctaLabel="Build My Ad Plan"
        ctaLoadingLabel="Building your plan..."
        ctaDisabled={!hasSource}
        isLoading={isLoading}
        onSubmit={handleContinue}
        continuationLabel={activeTab === 'search' && business ? `Continue as ${business.name}` : null}
        secondarySlot={
          activeTab === 'search' && business ? (
            <div className="flex items-start gap-3 p-3 bg-teal/5 border border-teal/20 rounded-lg">
              <Building2 className="h-5 w-5 text-teal mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{business.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{business.address}</div>
                {business.rating > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {business.rating} rating -- {business.reviewsCount || business.userRatingsTotal || 0} reviews
                  </div>
                )}
              </div>
            </div>
          ) : null
        }
        footerSlot={
          isMockMode() ? (
            <button
              className="mt-2 text-xs text-coral hover:text-coral/80 transition-colors w-full text-center font-medium"
              onClick={handleMockDemo}
              disabled={isLoading}
            >
              Demo: Use AutoZone Downtown
            </button>
          ) : null
        }
      >
        {/* Google Maps tab */}
        {activeTab === 'search' && (
          <div className="relative group">
            {apiKey && apiKey !== 'your_api_key_here' ? (
              <APIProvider apiKey={apiKey}>
                <PlacesAutocomplete
                  onPlaceSelect={handlePlaceSelect}
                  placeholder="Search by business name and location..."
                  types={['establishment']}
                  className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm transition-shadow group-hover:shadow-md"
                />
              </APIProvider>
            ) : (
              <div className="flex items-center gap-2 h-14 rounded-xl border border-input px-4 bg-muted text-muted-foreground text-sm">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>Google Maps search requires API key</span>
              </div>
            )}
          </div>
        )}

        {/* Website URL tab */}
        {activeTab === 'website' && (
          <div className="space-y-3">
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                <Globe className="size-5" />
              </div>
              <input
                type="url"
                className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background pl-12 pr-4 text-base shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral"
                placeholder="https://mybusiness.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                aria-label="Business website URL"
              />
            </div>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                <MapPin className="size-5" />
              </div>
              <input
                type="text"
                className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background pl-12 pr-4 text-base shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral"
                placeholder="Where should we show your ads? (address or zip code)"
                value={targetingAddress}
                onChange={(e) => setTargetingAddress(e.target.value)}
                aria-label="Ad targeting location"
              />
            </div>
          </div>
        )}
      </BusinessSearchCard>
    </>
  );
}
